// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import { Button, Typography } from '@mui/material'

// ** Child Imports
import FloGenericDialog from '../../common/feedback-and-dialog/GenericDialog'
import { orderDetailsMetadata } from 'src/lib/utils/rewards'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import GenericTable from 'src/components/common/table/GenericTable'

interface CustomerTransactionTableProps {
  value: any
  isLoading: boolean
  isPaginationEnabled: boolean
}

const ROWS_PER_PAGE = 5

const CustomerTransactionTable: React.FC<CustomerTransactionTableProps> = ({
  value,
  isLoading,
  isPaginationEnabled = true
}) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  })
  const page = pagination.pageIndex
  useEffect(() => {
    if (typeof value !== 'undefined') setPagination({ ...pagination, pageIndex: 0 })
  }, [value])

  const handlePageChange = async (newPage: number) => {
    setPagination(prev => ({
      ...prev,
      pageIndex: page
    }))
  }

  const [isCheckoutDetailsOpen, setIsCheckoutDetailsOpen] = useState<boolean>(false)
  const [selectedTransactionOrderData, setSelectedTransactionOrderData] = useState<any>()

  const columns: ColumnDef<any>[] = [
    {
      id: 'checkoutDetails',
      header: 'Event',
      cell: ({ row }) => {
        const checkoutDetails = row.original['checkoutDetails']
        const platformOrderId = checkoutDetails?.orderId
        const reason = row.original['reason']
        const transactionDate = row.original['createdAt']
        const reference = row.original[`reference`]

        return (
          <div className='flex flex-col'>
            <>
              {Boolean(reference === 'MANUAL_ADJUST') ? (
                <p className='text-sm font-normal text-coal-dark'>{`Manual${Boolean(reason) ? ` - ${reason}` : ''}`}</p>
              ) : (
                <p className='text-sm font-semibold text-primary-dark'>{`Order ${
                  platformOrderId ? platformOrderId : ''
                }`}</p>
              )}
            </>
            <p className='text-xs text-gray-dark'>{transactionDate}</p>
          </div>
        )
      }
    },

    {
      id: 'transactionAmount',
      header: 'Amount',
      cell: ({ row }) => {
        const value = row.original['transactionAmount']
        const transactionType = row.original['transactionType']
        const trasactionSymbol = transactionType === 'CREDIT' ? '+' : '–'
        return (
          <div className='flex flex-row items-end gap-4'>
            <span>{trasactionSymbol}</span>
            <p className='text-sm text-coal-dark'>{value}</p>
          </div>
        )
      }
    }
  ]

  useEffect(() => {
    if (!selectedTransactionOrderData) return
    setIsCheckoutDetailsOpen(true)
  }, [selectedTransactionOrderData])

  const renderCheckoutInfo = () => {
    if (!selectedTransactionOrderData) return
    return (
      <ul className='flex flex-wrap space-y-3'>
        {Object.entries(selectedTransactionOrderData ?? {}).map(([key, value]) => {
          const matchedKPI = orderDetailsMetadata.find((kpi: any) => kpi.key === key)
          if (matchedKPI) {
            return (
              <li className='flex flex-col w-1/2' key={key}>
                <span className='text-xs text-coal-light'>{matchedKPI.displayText}</span>
                <span className='text-sm text-coal-dark'>{value as any}</span>
              </li>
            )
          }
        })}
      </ul>
    )
  }

  return (
    <div className='flex flex-col w-full'>
      <GenericTable
        data={[...value?.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)]}
        columns={columns}
        pagination={{
          paginationState: pagination,
          setPaginationState: setPagination
        }}
        totalRows={value?.length ?? 0}
        loading={isLoading}
        customPaginationConfig={{
          customPagination: false
        }}
        isCustomizable={{
          isFullWidth: false,
          isResizable: true,
          hideDropDown: true
        }}
      />
      {isCheckoutDetailsOpen ? (
        <FloGenericDialog
          isOpen={isCheckoutDetailsOpen}
          width={500}
          handleClose={setIsCheckoutDetailsOpen}
          closeButtonText={'Close'}
          content={<>{renderCheckoutInfo()}</>}
          title={`Checkout info`}
          closeButtonVariant='outlined'
          hideCloseIcon={true}
        />
      ) : (
        <></>
      )}
    </div>
  )
}

export default CustomerTransactionTable
